import { FC, PropsWithChildren, useEffect, useMemo, useState } from 'react'

import { Summary as SummaryType } from '@img-arena/consoles-api-streaming'
import { Skeleton, Stack, Typography, useTheme } from '@mui/material'
import { streamingRestApi } from '@/service/streamingApi'
import { bytesToHumanReadable } from '@/utils/bytesToHumanReadable'
import { formatNumberToSI } from '@/utils/formatNumberToSI'
import { secondsToHMS } from '@/utils/secondsToHMS'

import { ComponentWithStatisticsQueryParams } from '../types'

export const STATISTICS_SUMMARY_CARD_TEST_ID = 'statistics-summary-card' as const
export const STATISTICS_SUMMARY_SKELETON_TEST_ID = 'statistics-summary-skeleton' as const

const randomWidth = () => {
  const minWidth = 120
  const maxWidth = 230

  return Math.floor(Math.random() * (maxWidth - minWidth + 1)) + minWidth
}

const Container: FC<PropsWithChildren & { testId?: string }> = ({ children, testId }) => {
  const theme = useTheme()

  return (
    <Stack
      data-testid={testId}
      direction="row"
      alignItems="center"
      flexWrap="wrap"
      sx={{
        backgroundColor: theme.palette.statistics.panel.header.backgroundColor,
        borderTopLeftRadius: Number(theme.shape.borderRadius) * 2,
        borderTopRightRadius: Number(theme.shape.borderRadius) * 2,
        minHeight: theme.palette.card.header.minHeight,
        p: 2
      }}
      gap={2}
    >
      {children}
    </Stack>
  )
}

export const SUMMARY_LABELS_MAP: Record<keyof SummaryType, string> = {
  totalViews: 'Total Views',
  totalDuration: 'Total min viewed',
  averageDuration: 'AVG Duration',
  uniqueViews: 'Unique Views',
  totalBytes: 'Bandwidth consumed',
  desktopViews: '',
  mobileViews: ''
}

export const StatisticsSummary: FC<ComponentWithStatisticsQueryParams> = ({ queryParams }) => {
  const analyticsEndpoints = streamingRestApi.endpoints.analytics
  const [summary, setSummary] = useState<SummaryType | null>(null)

  useEffect(() => {
    const ac = new AbortController()

    analyticsEndpoints.getSummary(queryParams, ac.signal).then((data) => {
      setSummary(data)
    })

    return () => {
      ac.abort()
    }
  }, [...Object.values(queryParams)])

  const summaryInfo = useMemo(() => {
    if (!summary) return []

    return [
      {
        label: SUMMARY_LABELS_MAP.totalViews,
        value: formatNumberToSI(summary.totalViews, { space: true })
      },
      {
        label: SUMMARY_LABELS_MAP.totalDuration,
        value: formatNumberToSI(summary.totalDuration / 60, { space: true })
      },
      {
        label: SUMMARY_LABELS_MAP.averageDuration,
        value: secondsToHMS(summary.averageDuration)
      },
      {
        label: SUMMARY_LABELS_MAP.uniqueViews,
        value: formatNumberToSI(summary.uniqueViews, { space: true })
      },
      {
        label: SUMMARY_LABELS_MAP.totalBytes,
        value: bytesToHumanReadable(summary.totalBytes, {
          space: true
        })
      }
    ]
  }, [summary])

  if (!summary) {
    return (
      <Container testId={STATISTICS_SUMMARY_SKELETON_TEST_ID}>
        {Array.from({ length: 5 }).map((_, index) => (
          <Typography
            variant="body1"
            key={`summary-skeleton-${index}`}
            sx={{
              width: randomWidth()
            }}
          >
            <Skeleton />
          </Typography>
        ))}
      </Container>
    )
  }

  return (
    <Container testId={STATISTICS_SUMMARY_CARD_TEST_ID}>
      {summaryInfo.map((item) => (
        <Stack key={item.label} direction="row" alignItems="center" gap={1}>
          <Typography variant="body1">{item.label}:</Typography>
          <Typography variant="body1" fontWeight="bold">
            {item.value}
          </Typography>
        </Stack>
      ))}
    </Container>
  )
}
